import React, { useState, useEffect, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiAlertCircle, FiUpload, FiRefreshCw, FiPercent, FiBox } from 'react-icons/fi';
import api from '../services/api';
import Modal from '../components/Modal';
import { useSearch } from '../contexts/SearchContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../components/Button.css';
import './Producten.css';
import ProductenPrijsberekeningTab from './ProductenPrijsberekeningTab';
import ProductenOpslagTab from './ProductenOpslagTab';
import { ProductNaamMetKlasse, PRODUCT_KLASSE_META } from '../components/ProductKlasseIcon';
import ProductKlasseIcon from '../components/ProductKlasseIcon';

const Producten = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [producten, setProducten] = useState([]);
  const [leveranciers, setLeveranciers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingInkoopprijs, setEditingInkoopprijs] = useState(null); // { productId: value }
  const [showCBMImportModal, setShowCBMImportModal] = useState(false);
  const [cbmImportData, setCbmImportData] = useState('');
  const [cbmImportLoading, setCbmImportLoading] = useState(false);
  const [showInkoopprijsImportModal, setShowInkoopprijsImportModal] = useState(false);
  const [inkoopprijsImportData, setInkoopprijsImportData] = useState('');
  const [inkoopprijsImportLoading, setInkoopprijsImportLoading] = useState(false);
  const [inkoopprijsImportMode, setInkoopprijsImportMode] = useState('paste'); // 'paste' | 'file'
  const { searchTerm } = useSearch();
  const [importingWoo, setImportingWoo] = useState(false);
  const [productenSubTab, setProductenSubTab] = useState('lijst');
  const [formData, setFormData] = useState({
    naam: '',
    sku: '',
    ean: '',
    beschrijving: '',
    categorie: '',
    prijs: '',
    inkoopprijs: '',
    voorraad: '',
    voorraad_onderweg: '',
    in_productie: '',
    minimum_voorraad: '',
    min_inkoop_aantal: '30',
    cbm_per_stuk: '',
    inhoud_liter: '',
    uitfaseren: false,
    leverancier_id: '',
    levertijd_dagen_override: ''
  });

  useEffect(() => {
    fetchProducten();
    fetchLeveranciers();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'opslag' || tab === 'prijsberekening' || tab === 'lijst') {
      setProductenSubTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (productenSubTab === 'prijsberekening') {
      fetchLeveranciers();
    }
  }, [productenSubTab]);

  const fetchProducten = async () => {
    try {
      const response = await api.get('/producten');
      const products = Array.isArray(response.data) ? response.data : [];
      console.log('[Producten] Loaded products:', products.length);
      if (products.length > 0) {
        console.log('[Producten] Sample product:', products[0]);
        // Debug: check in_productie values
        const productsWithInProductie = products.filter(p => (p.in_productie || 0) > 0);
        console.log('[Producten] Products with in_productie > 0:', productsWithInProductie.length);
        if (productsWithInProductie.length > 0) {
          console.log('[Producten] Sample product with in_productie:', productsWithInProductie[0]);
        }
      }
      setProducten(products);
    } catch (error) {
      console.error('Error fetching producten:', error);
      setProducten([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchLeveranciers = async () => {
    try {
      const response = await api.get('/leveranciers');
      setLeveranciers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching leveranciers:', error);
      setLeveranciers([]); // Set to empty array on error
    }
  };

  const handleUpdateInkoopprijs = async (productId, newValue) => {
    try {
      const product = producten.find(p => p.id === productId);
      if (!product) return;

      const updatedProduct = {
        ...product,
        inkoopprijs: parseFloat(newValue) || null
      };

      await api.put(`/producten/${productId}`, updatedProduct);
      
      // Update local state
      setProducten(producten.map(p => 
        p.id === productId ? { ...p, inkoopprijs: parseFloat(newValue) || null } : p
      ));
      
      setEditingInkoopprijs(null);
    } catch (error) {
      console.error('Error updating inkoopprijs:', error);
      alert('Fout bij bijwerken inkoopprijs: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
          const data = {
            ...formData,
            prijs: parseFloat(formData.prijs) || 0,
            inkoopprijs: parseFloat(formData.inkoopprijs) || null,
            voorraad: parseInt(formData.voorraad) || 0,
            voorraad_onderweg: parseInt(formData.voorraad_onderweg) || 0,
            in_productie: parseInt(formData.in_productie) || 0,
            minimum_voorraad: parseInt(formData.minimum_voorraad) || 0,
            min_inkoop_aantal: parseInt(formData.min_inkoop_aantal) || 30,
            cbm_per_stuk: formData.cbm_per_stuk && formData.cbm_per_stuk !== '' ? parseFloat(formData.cbm_per_stuk) : null,
            inhoud_liter:
              formData.inhoud_liter != null && String(formData.inhoud_liter).trim() !== ''
                ? parseFloat(String(formData.inhoud_liter).replace(',', '.'))
                : null,
            uitfaseren: formData.uitfaseren || false,
            leverancier_id: formData.leverancier_id || null,
            levertijd_dagen_override:
              formData.levertijd_dagen_override != null && String(formData.levertijd_dagen_override).trim() !== ''
                ? parseInt(formData.levertijd_dagen_override, 10)
                : null
          };

      if (editingProduct) {
        await api.put(`/producten/${editingProduct.id}`, data);
      } else {
        await api.post('/producten', data);
      }

      setShowModal(false);
      resetForm();
      fetchProducten();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      naam: product.naam || '',
      sku: product.sku || '',
      ean: product.ean || '',
      beschrijving: product.beschrijving || '',
      categorie: product.categorie || '',
      prijs: product.prijs || '',
      inkoopprijs: product.inkoopprijs || '',
      voorraad: product.voorraad || '',
      voorraad_onderweg: product.voorraad_onderweg || '',
      in_productie: product.in_productie || '',
      minimum_voorraad: product.minimum_voorraad || '',
      min_inkoop_aantal: product.min_inkoop_aantal || 30,
      cbm_per_stuk: product.cbm_per_stuk || '',
      inhoud_liter: product.inhoud_liter || '',
      uitfaseren: product.uitfaseren === 1,
      leverancier_id: product.leverancier_id || '',
      levertijd_dagen_override:
        product.levertijd_dagen_override != null && product.levertijd_dagen_override !== ''
          ? String(product.levertijd_dagen_override)
          : ''
    });
    setShowModal(true);
  };

  const handleImportWooProducts = async () => {
    if (!window.confirm('Alle producten uit WooCommerce importeren/bijwerken?')) return;
    setImportingWoo(true);
    try {
      const res = await api.post('/woocommerce/import', {}, { timeout: 300000 });
      const { successful = 0, failed = 0, total = 0 } = res.data || {};
      alert(`Import voltooid:\nTotaal: ${total}\nSuccesvol: ${successful}\nMislukt: ${failed}`);
      fetchProducten();
    } catch (error) {
      alert('Fout bij importeren: ' + (error.response?.data?.message || error.message));
    } finally {
      setImportingWoo(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit product wilt verwijderen?')) {
      return;
    }

    try {
      await api.delete(`/producten/${id}`);
      fetchProducten();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRowClick = async (product, e) => {
    // Prevent opening detail when clicking on action buttons or inputs
    if (e.target.closest('.action-buttons') || e.target.closest('input') || e.target.closest('button')) {
      return;
    }

    navigate(`/producten/${product.id}`);
  };

  const handleCBMImport = async () => {
    if (!cbmImportData.trim()) {
      alert('Voer CBM data in');
      return;
    }

    setCbmImportLoading(true);
    
    try {
      // Parse de input: verwacht format zoals "EAN1,CBM1\nEAN2,CBM2" of tab/space gescheiden
      // Ondersteunt ook komma als decimaalteken (0,14 -> 0.14)
      const lines = cbmImportData.trim().split('\n').filter(line => line.trim());
      const cbmData = [];
      
      lines.forEach((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        
        // Probeer verschillende scheidingstekens: tab, komma, spatie, puntkomma
        // Eerst splitsen op whitespace (spatie of tab), anders op komma/puntkomma
        let parts;
        if (trimmed.includes(' ') || trimmed.includes('\t')) {
          // Splits op whitespace (spatie of tab)
          parts = trimmed.split(/[\s\t]+/).map(p => p.trim()).filter(p => p);
        } else {
          // Splits op komma of puntkomma
          parts = trimmed.split(/[,;]/).map(p => p.trim()).filter(p => p);
        }
        
        if (parts.length >= 2) {
          const ean = parts[0];
          // Vervang komma door punt voor decimaalteken (0,14 -> 0.14)
          const cbmString = parts[1].replace(',', '.');
          const cbm = parseFloat(cbmString);
          
          if (!isNaN(cbm) && cbm >= 0) {
            cbmData.push({ ean, cbm_per_stuk: cbm });
          } else {
            console.warn(`Regel ${index + 1}: Ongeldige CBM waarde: ${parts[1]}`);
          }
        } else if (parts.length === 1) {
          // Probeer als laatste redmiddel: splits op eerste spatie of komma
          const match = trimmed.match(/^(\d+)[\s,]+([\d,]+)$/);
          if (match) {
            const ean = match[1];
            const cbmString = match[2].replace(',', '.');
            const cbm = parseFloat(cbmString);
            if (!isNaN(cbm) && cbm >= 0) {
              cbmData.push({ ean, cbm_per_stuk: cbm });
            } else {
              console.warn(`Regel ${index + 1}: Ongeldige CBM waarde: ${match[2]}`);
            }
          } else {
            console.warn(`Regel ${index + 1}: Ongeldig format: ${trimmed}`);
          }
        } else {
          console.warn(`Regel ${index + 1}: Ongeldig format: ${trimmed}`);
        }
      });
      
      if (cbmData.length === 0) {
        alert('Geen geldige CBM data gevonden. Format: EAN,CBM (één per regel)');
        setCbmImportLoading(false);
        return;
      }
      
      // Verstuur naar backend
      const response = await api.post('/producten/bulk-update-cbm', { cbmData });
      
      // Toon resultaten
      const successCount = response.data.success.length;
      const notFoundCount = response.data.notFound.length;
      const errorCount = response.data.errors.length;
      
      let message = `CBM import voltooid:\n`;
      message += `✓ ${successCount} product(en) bijgewerkt\n`;
      if (notFoundCount > 0) {
        message += `⚠ ${notFoundCount} EAN(s) niet gevonden\n`;
      }
      if (errorCount > 0) {
        message += `✗ ${errorCount} fout(en)`;
      }
      
      alert(message);
      
      // Refresh producten lijst
      fetchProducten();
      
      // Reset form
      setCbmImportData('');
      setShowCBMImportModal(false);
      
    } catch (error) {
      console.error('Error importing CBM:', error);
      alert('Fout bij importeren: ' + (error.response?.data?.error || error.message));
    } finally {
      setCbmImportLoading(false);
    }
  };

  const parseInkoopprijsData = (text) => {
    const lines = text.trim().split('\n').filter(line => line.trim());
    const data = [];
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      let parts;
      if (trimmed.includes('\t') || trimmed.includes(' ')) {
        parts = trimmed.split(/[\s\t]+/).map(p => p.trim()).filter(p => p);
      } else {
        parts = trimmed.split(/[,;]/).map(p => p.trim()).filter(p => p);
      }
      if (parts.length >= 2) {
        const ean = parts[0];
        const prijsStr = parts[1].replace(',', '.');
        const inkoopprijs = parseFloat(prijsStr);
        if (!isNaN(inkoopprijs) && inkoopprijs >= 0) {
          data.push({ ean, inkoopprijs });
        }
      }
    });
    return data;
  };

  const handleInkoopprijsImport = async (dataToImport) => {
    if (!dataToImport || dataToImport.length === 0) {
      alert('Geen geldige data. Format: EAN en inkoopprijs per regel (tab, komma of spatie als scheidingsteken).');
      return;
    }
    setInkoopprijsImportLoading(true);
    try {
      const response = await api.post('/producten/bulk-update-inkoopprijs', { inkopData: dataToImport });
      const successCount = response.data.success.length;
      const notFoundCount = response.data.notFound.length;
      const errorCount = response.data.errors.length;
      let message = `Inkoopprijzen import voltooid:\n`;
      message += `✓ ${successCount} product(en) bijgewerkt\n`;
      if (notFoundCount > 0) message += `⚠ ${notFoundCount} EAN(s) niet gevonden\n`;
      if (errorCount > 0) message += `✗ ${errorCount} fout(en)`;
      alert(message);
      fetchProducten();
      setInkoopprijsImportData('');
      setShowInkoopprijsImportModal(false);
    } catch (error) {
      console.error('Error importing inkoopprijzen:', error);
      alert('Fout bij importeren: ' + (error.response?.data?.error || error.message));
    } finally {
      setInkoopprijsImportLoading(false);
    }
  };

  const handleInkoopprijsPasteImport = async () => {
    const data = parseInkoopprijsData(inkoopprijsImportData);
    await handleInkoopprijsImport(data);
  };

  const handleInkoopprijsFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      if (!rows || rows.length === 0) {
        alert('Excel bestand is leeg of kon niet worden gelezen.');
        return;
      }
      const headerRow = rows[0] || [];
      let eanColIdx = -1;
      let prijsColIdx = -1;
      const eanKeywords = ['ean', 'barcode', 'gtin', 'artikelnummer', 'artikel', 'sku'];
      const prijsKeywords = ['inkoopprijs', 'inkoop', 'prijs', 'cost', 'eenheidsprijs'];
      headerRow.forEach((h, i) => {
        const lower = String(h || '').toLowerCase();
        if (eanKeywords.some(k => lower.includes(k)) || /^\d{8,14}$/.test(String(h || '').replace(/\D/g, ''))) {
          if (eanColIdx < 0) eanColIdx = i;
        }
        if (prijsKeywords.some(k => lower.includes(k))) {
          if (prijsColIdx < 0) prijsColIdx = i;
        }
      });
      if (eanColIdx < 0) eanColIdx = 0;
      if (prijsColIdx < 0) {
        for (let i = 0; i < headerRow.length; i++) {
          if (i !== eanColIdx) {
            const val = rows[1]?.[i];
            if (val != null && !isNaN(parseFloat(String(val).replace(',', '.')))) {
              prijsColIdx = i;
              break;
            }
          }
        }
        if (prijsColIdx < 0) prijsColIdx = 1;
      }
      const inkopData = [];
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r] || [];
        const ean = String(row[eanColIdx] ?? '').trim();
        const prijsVal = row[prijsColIdx];
        if (!ean) continue;
        const prijs = parseFloat(String(prijsVal ?? '').replace(',', '.')) || 0;
        if (prijs >= 0) inkopData.push({ ean, inkoopprijs: prijs });
      }
      await handleInkoopprijsImport(inkopData);
    } catch (err) {
      console.error('Excel parse error:', err);
      alert('Kon Excel niet lezen. Gebruik .xlsx formaat of plak de data uit Excel.');
    } finally {
      e.target.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      naam: '',
      sku: '',
      ean: '',
      beschrijving: '',
      categorie: '',
      prijs: '',
      inkoopprijs: '',
      voorraad: '',
      voorraad_onderweg: '',
      minimum_voorraad: '',
      min_inkoop_aantal: '30',
      cbm_per_stuk: '',
      inhoud_liter: '',
      uitfaseren: false,
      leverancier_id: '',
      levertijd_dagen_override: ''
    });
    setEditingProduct(null);
  };


  const isLowStock = (product) => {
    return product.voorraad <= product.minimum_voorraad;
  };

  // Filter en sorteer producten
  const filteredProducten = useMemo(() => {
    if (!Array.isArray(producten)) return [];
    
    let filtered = producten;
    
    // Filter op zoekterm als die er is
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      
      filtered = producten.filter(product => {
      // Zoek in product naam (case-insensitive, partial match)
      const naam = String(product.naam || '').toLowerCase();
      const naamMatch = naam.includes(searchLower);
      
      // Zoek ook in beschrijving
      const beschrijving = String(product.beschrijving || '').toLowerCase();
      const beschrijvingMatch = beschrijving.includes(searchLower);
      
      // Zoek in SKU (case-insensitive)
      const sku = String(product.sku || '').toLowerCase();
      const skuMatch = sku.includes(searchLower);
      
      // Zoek in EAN (normalize: verwijder alle niet-cijfers voor vergelijking)
      const eanNormalized = String(product.ean || '').replace(/\D/g, '');
      const searchNormalized = searchLower.replace(/\D/g, '');
      const eanMatch = searchNormalized.length > 0 && (
        eanNormalized.includes(searchNormalized) || 
        (searchNormalized.length >= 3 && eanNormalized.includes(searchNormalized))
      );
      
      // Zoek in SKU numeriek (als SKU EAN bevat)
      const skuNormalized = String(product.sku || '').replace(/\D/g, '');
      const skuNumericMatch = searchNormalized.length > 0 && (
        skuNormalized.includes(searchNormalized) ||
        (searchNormalized.length >= 3 && skuNormalized.includes(searchNormalized))
      );
      
      const matches = naamMatch || beschrijvingMatch || skuMatch || eanMatch || skuNumericMatch;
      
      // Debug logging (alleen in development)
      if (process.env.NODE_ENV === 'development' && searchTerm.toLowerCase() === 'ravi') {
        console.log('Search debug for "ravi":', {
          product: product.naam,
          naam,
          naamMatch,
          beschrijvingMatch,
          skuMatch,
          eanMatch,
          skuNumericMatch,
          matches
        });
      }
      
      return matches;
    });
    }
    
    // Sorteer: uitgefaseerde producten onderaan
    return filtered.sort((a, b) => {
      const aUitfaseren = a.uitfaseren === 1 ? 1 : 0;
      const bUitfaseren = b.uitfaseren === 1 ? 1 : 0;
      
      // Als beide hetzelfde zijn (beide uitgefaseerd of beide niet), sorteer op naam
      if (aUitfaseren === bUitfaseren) {
        return (a.naam || '').localeCompare(b.naam || '', 'nl', { sensitivity: 'base' });
      }
      
      // Uitgefaseerde producten komen onderaan (1 komt na 0)
      return aUitfaseren - bUitfaseren;
    });
  }, [producten, searchTerm]);

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="producten">
      <div className="page-header">
        <div>
          <h1 className="page-title">Producten</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.35rem', fontSize: '0.85rem', color: '#64748b' }}>
            {Object.entries(PRODUCT_KLASSE_META).map(([key, meta]) => (
              <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                <ProductKlasseIcon klasse={key} size={13} />
                {meta.label}
              </span>
            ))}
          </div>
        </div>
        <div className="producten-header-actions">
          <button className="btn btn-secondary" onClick={handleImportWooProducts} disabled={importingWoo}>
            <FiRefreshCw /> {importingWoo ? 'Importeren...' : 'Importeer uit WooCommerce'}
          </button>
          <button className="btn btn-secondary producten-desktop-only-action" onClick={() => setShowCBMImportModal(true)}>
            <FiUpload /> CBM Importeren
          </button>
          <button className="btn btn-secondary producten-desktop-only-action" onClick={() => setShowInkoopprijsImportModal(true)}>
            <FiUpload /> Inkoopprijzen Importeren
          </button>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <FiPlus /> Nieuw Product
          </button>
        </div>
      </div>

      {searchTerm && productenSubTab === 'lijst' && (
        <div className="search-results-info" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0fffe', borderRadius: '8px', border: '1px solid #3FDFCF' }}>
          <strong>{filteredProducten.length}</strong> van <strong>{producten.length}</strong> product(en) gevonden voor "{searchTerm}"
        </div>
      )}

      <div className="producten-subtabs" role="tablist" aria-label="Producten weergave">
        <button
          type="button"
          role="tab"
          aria-selected={productenSubTab === 'lijst'}
          className={`producten-subtab ${productenSubTab === 'lijst' ? 'active' : ''}`}
          onClick={() => setProductenSubTab('lijst')}
        >
          Productlijst
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={productenSubTab === 'prijsberekening'}
          className={`producten-subtab ${productenSubTab === 'prijsberekening' ? 'active' : ''}`}
          onClick={() => setProductenSubTab('prijsberekening')}
        >
          <FiPercent aria-hidden /> Prijsberekening (advies)
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={productenSubTab === 'opslag'}
          className={`producten-subtab ${productenSubTab === 'opslag' ? 'active' : ''}`}
          onClick={() => setProductenSubTab('opslag')}
        >
          <FiBox aria-hidden /> Opslagkosten (Heeren vs AG)
        </button>
      </div>

      {productenSubTab === 'opslag' ? (
        <ProductenOpslagTab searchTerm={searchTerm} />
      ) : productenSubTab === 'prijsberekening' ? (
        <ProductenPrijsberekeningTab
          products={filteredProducten}
          leveranciers={leveranciers}
          searchTerm={searchTerm}
          onRefreshProducts={fetchProducten}
        />
      ) : (
        <>
      {/* Mobile-first: card list (desktop table blijft bestaan) */}
      <div className="producten-mobile-list">
        {filteredProducten.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? `Geen producten gevonden voor "${searchTerm}"` : 'Geen producten gevonden'}
          </div>
        ) : (
          filteredProducten.map((p) => {
            const voorraad = parseInt(p.voorraad, 10) || 0;
            const onderweg = parseInt(p.voorraad_onderweg, 10) || 0;
            const prod = parseInt(p.in_productie, 10) || 0;
            const totaal = voorraad + onderweg + prod;
            const min = parseInt(p.minimum_voorraad, 10) || 0;
            const low = voorraad <= min;
            return (
              <button
                type="button"
                key={p.id}
                className={`product-card ${low ? 'low' : ''} ${p.uitfaseren === 1 ? 'uit' : ''}`}
                onClick={() => navigate(`/producten/${p.id}`)}
              >
                <div className="product-card-top">
                  <div className="product-card-title">
                    <span className="product-card-name">
                      <ProductNaamMetKlasse productId={p.id} naam={p.naam} product_klasse={p.product_klasse} />
                    </span>
                    {low ? <FiAlertCircle className="product-card-alert" title="Lage voorraad" /> : null}
                  </div>
                  <div className="product-card-total">
                    <span className="muted">Totaal</span>
                    <strong>{totaal}</strong>
                  </div>
                </div>

                <div className="product-card-meta">
                  {p.sku ? <span className="chip">SKU: {p.sku}</span> : null}
                  {p.ean ? <span className="chip">EAN: {p.ean}</span> : null}
                  {p.leverancier_naam ? <span className="chip">Lev: {p.leverancier_naam}</span> : null}
                  {p.uitfaseren === 1 ? <span className="chip chip-muted">Uitgefaseerd</span> : null}
                </div>

                <div className="product-card-stats">
                  <div className="stat"><span>V</span><strong>{voorraad}</strong></div>
                  <div className="stat"><span>O</span><strong>{onderweg}</strong></div>
                  <div className="stat"><span>P</span><strong>{prod}</strong></div>
                  <div className="stat"><span>Min</span><strong>{min}</strong></div>
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="table-container producten-desktop-table">
        <table className="data-table">
          <thead>
            <tr>
              <th>Naam</th>
              <th>SKU</th>
              <th>Categorie</th>
              <th>Verkoop</th>
              <th>Inkoop</th>
              <th>Voorr.</th>
              <th>Onderw.</th>
              <th>Prod.</th>
              <th>Totaal</th>
              <th>Min. Voorr.</th>
              <th>Min. Ink.</th>
              <th>CBM</th>
              <th>Uitfas.</th>
              <th>Leverancier</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducten.length === 0 ? (
              <tr>
                <td colSpan="15" className="empty-state">
                  {searchTerm ? `Geen producten gevonden voor "${searchTerm}"` : 'Geen producten gevonden'}
                </td>
              </tr>
            ) : (
              filteredProducten.map((product) => {
                // Ensure numeric values
                const voorraad = parseInt(product.voorraad) || 0;
                const voorraadOnderweg = parseInt(product.voorraad_onderweg) || 0;
                const inProductie = parseInt(product.in_productie) || 0;
                const totaleVoorraad = voorraad + voorraadOnderweg + inProductie;
                
                return (
                  <tr 
                    key={product.id} 
                    className={`product-row ${isLowStock(product) ? 'low-stock' : ''}`}
                    onClick={(e) => handleRowClick(product, e)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="product-name">
                        <ProductNaamMetKlasse productId={product.id} naam={product.naam} product_klasse={product.product_klasse} />
                        {isLowStock(product) && (
                          <FiAlertCircle className="alert-icon" title="Lage voorraad" />
                        )}
                      </div>
                    </td>
                    <td>{product.sku || '-'}</td>
                    <td>{product.categorie || '-'}</td>
                    <td>€{parseFloat(product.prijs || 0).toFixed(2)}</td>
                    <td 
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingInkoopprijs({ productId: product.id, value: product.inkoopprijs || '' });
                      }}
                      style={{ cursor: 'pointer', position: 'relative' }}
                      title="Klik om te bewerken"
                    >
                      {editingInkoopprijs?.productId === product.id ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editingInkoopprijs.value}
                          onChange={(e) => setEditingInkoopprijs({ ...editingInkoopprijs, value: e.target.value })}
                          onBlur={() => {
                            if (editingInkoopprijs.value !== (product.inkoopprijs || '')) {
                              handleUpdateInkoopprijs(product.id, editingInkoopprijs.value);
                            } else {
                              setEditingInkoopprijs(null);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            } else if (e.key === 'Escape') {
                              setEditingInkoopprijs(null);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          style={{ width: '80px', padding: '2px 4px', border: '1px solid #3FDFCF', borderRadius: '4px' }}
                        />
                      ) : (
                        <span>€{parseFloat(product.inkoopprijs || 0).toFixed(2)}</span>
                      )}
                    </td>
                    <td>{voorraad}</td>
                    <td className={voorraadOnderweg > 0 ? 'in-transit' : ''}>
                      {voorraadOnderweg}
                    </td>
                    <td className={inProductie > 0 ? 'in-production' : ''}>
                      {inProductie}
                    </td>
                    <td className="total-stock">
                      <strong>{totaleVoorraad}</strong>
                    </td>
                    <td>{product.minimum_voorraad}</td>
                    <td>{product.min_inkoop_aantal || 30}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                        {product.cbm_per_stuk !== null && product.cbm_per_stuk !== undefined && product.cbm_per_stuk !== '' && product.cbm_per_stuk !== 0 ? (
                          <span style={{ fontWeight: '600', color: '#3FDFCF', fontSize: '0.9rem' }}>
                            {parseFloat(product.cbm_per_stuk).toFixed(3)}
                          </span>
                        ) : (
                          <span style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>-</span>
                        )}
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={product.cbm_per_stuk !== null && product.cbm_per_stuk !== undefined && product.cbm_per_stuk !== '' && product.cbm_per_stuk !== 0 ? parseFloat(product.cbm_per_stuk) : ''}
                          onChange={async (e) => {
                            const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
                            try {
                              await api.put(`/producten/${product.id}`, {
                                ...product,
                                cbm_per_stuk: newValue
                              });
                              fetchProducten();
                            } catch (error) {
                              console.error('Error updating CBM:', error);
                              alert('Fout bij bijwerken: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          onBlur={async (e) => {
                            // Sla op bij blur (verlaten veld)
                            const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
                            if (newValue !== (product.cbm_per_stuk || null)) {
                              try {
                                await api.put(`/producten/${product.id}`, {
                                  ...product,
                                  cbm_per_stuk: newValue
                                });
                                fetchProducten();
                              } catch (error) {
                                console.error('Error updating CBM:', error);
                              }
                            }
                          }}
                          style={{ 
                            width: '80px',
                            padding: '0.35rem', 
                            textAlign: 'center',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            fontFamily: 'inherit'
                          }}
                          placeholder="0.000"
                          title="CBM per stuk - klik om te bewerken"
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={product.uitfaseren === 1}
                        onChange={async (e) => {
                          try {
                            await api.put(`/producten/${product.id}`, {
                              ...product,
                              uitfaseren: e.target.checked ? 1 : 0
                            });
                            fetchProducten();
                          } catch (error) {
                            console.error('Error updating uitfaseren:', error);
                            alert('Fout bij bijwerken: ' + (error.response?.data?.error || error.message));
                          }
                        }}
                        title="Uitfaseren - product wordt niet meer besteld"
                      />
                    </td>
                    <td>{product.leverancier_naam || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" onClick={(e) => { e.stopPropagation(); handleEdit(product); }}>
                          <FiEdit />
                        </button>
                        <button className="btn-icon btn-danger" onClick={(e) => { e.stopPropagation(); handleDelete(product.id); }}>
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
        </>
      )}

      <Modal show={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={editingProduct ? 'Product Bewerken' : 'Nieuw Product'}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>Naam *</label>
            <input
              type="text"
              value={formData.naam}
              onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>SKU</label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>EAN</label>
              <input
                type="text"
                value={formData.ean}
                onChange={(e) => setFormData({ ...formData, ean: e.target.value })}
                placeholder="8-14 cijfers"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Beschrijving</label>
            <textarea
              value={formData.beschrijving}
              onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Categorie</label>
              <input
                type="text"
                value={formData.categorie}
                onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Verkoopprijs (€)</label>
              <input
                type="number"
                step="0.01"
                value={formData.prijs}
                onChange={(e) => setFormData({ ...formData, prijs: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Inkoopprijs (€)</label>
              <input
                type="number"
                step="0.01"
                value={formData.inkoopprijs}
                onChange={(e) => setFormData({ ...formData, inkoopprijs: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Voorraad</label>
              <input
                type="number"
                value={formData.voorraad}
                onChange={(e) => setFormData({ ...formData, voorraad: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Voorraad Onderweg</label>
              <input
                type="number"
                value={formData.voorraad_onderweg}
                onChange={(e) => setFormData({ ...formData, voorraad_onderweg: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>In Productie</label>
              <input
                type="number"
                value={formData.in_productie}
                onChange={(e) => setFormData({ ...formData, in_productie: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Minimum Voorraad</label>
              <input
                type="number"
                value={formData.minimum_voorraad}
                onChange={(e) => setFormData({ ...formData, minimum_voorraad: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Min. Inkoop Aantal</label>
              <input
                type="number"
                value={formData.min_inkoop_aantal}
                onChange={(e) => setFormData({ ...formData, min_inkoop_aantal: e.target.value })}
                placeholder="30"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>CBM per Stuk</label>
              <input
                type="number"
                step="0.001"
                value={formData.cbm_per_stuk}
                onChange={(e) => setFormData({ ...formData, cbm_per_stuk: e.target.value })}
                placeholder="0.000"
              />
            </div>

            <div className="form-group">
              <label>Inhoud (liter)</label>
              <input
                type="number"
                step="0.01"
                value={formData.inhoud_liter}
                onChange={(e) => setFormData({ ...formData, inhoud_liter: e.target.value })}
                placeholder="Optioneel — anders CBM × 1000"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.uitfaseren}
                  onChange={(e) => setFormData({ ...formData, uitfaseren: e.target.checked })}
                  style={{ marginRight: '0.5rem' }}
                />
                Uitfaseren (product wordt niet meer besteld)
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Leverancier</label>
            <select
              value={formData.leverancier_id}
              onChange={(e) => setFormData({ ...formData, leverancier_id: e.target.value })}
            >
              <option value="">Geen leverancier</option>
              {Array.isArray(leveranciers) && leveranciers.map((lev) => (
                <option key={lev.id} value={lev.id}>{lev.naam}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Inkooplevertijd override (dagen)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.levertijd_dagen_override}
              onChange={(e) => setFormData({ ...formData, levertijd_dagen_override: e.target.value })}
              placeholder="Leeg = levertijd van leverancier"
            />
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.35rem', marginBottom: 0 }}>
              Bijv. 90 voor langlopende orders. Overschrijft alleen voor dit product; anders geldt de levertijd op de leverancierskaart (of standaard 7 dagen als die leeg is).
            </p>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Annuleren
            </button>
            <button type="submit" className="btn btn-primary">
              {editingProduct ? 'Bijwerken' : 'Toevoegen'}
            </button>
            </div>
          </form>
        </Modal>

        {/* CBM Import Modal */}
        <Modal 
          show={showCBMImportModal} 
          onClose={() => { 
            setShowCBMImportModal(false);
            setCbmImportData('');
          }} 
          title="CBM Importeren"
          large={true}
        >
          <div style={{ padding: '1rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                Plak hier CBM data in het formaat: <strong>EAN CBM</strong> of <strong>EAN,CBM</strong> (één per regel)
              </p>
              <p style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1rem' }}>
                Voorbeelden (beide formaten werken):<br />
                8721001690862 0,125<br />
                8721001691791,0.150<br />
                8721001691937 0.200
              </p>
              <p style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                Tip: Komma's als decimaalteken worden automatisch geconverteerd naar punten.
              </p>
              <textarea
                value={cbmImportData}
                onChange={(e) => setCbmImportData(e.target.value)}
                placeholder="8721001690862,0.125&#10;8721001691791,0.150&#10;8721001691937,0.200"
                style={{
                  width: '100%',
                  minHeight: '300px',
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowCBMImportModal(false);
                  setCbmImportData('');
                }}
                disabled={cbmImportLoading}
              >
                Annuleren
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCBMImport}
                disabled={cbmImportLoading || !cbmImportData.trim()}
              >
                <FiUpload /> {cbmImportLoading ? 'Importeren...' : 'Importeren'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Inkoopprijzen Import Modal */}
        <Modal 
          show={showInkoopprijsImportModal} 
          onClose={() => { 
            setShowInkoopprijsImportModal(false);
            setInkoopprijsImportData('');
          }} 
          title="Inkoopprijzen Importeren"
          large={true}
        >
          <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <button 
                className={`btn ${inkoopprijsImportMode === 'paste' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setInkoopprijsImportMode('paste')}
              >
                Plak uit Excel
              </button>
              <button 
                className={`btn ${inkoopprijsImportMode === 'file' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setInkoopprijsImportMode('file')}
              >
                Upload Excel bestand
              </button>
            </div>
            {inkoopprijsImportMode === 'paste' ? (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ color: '#666', marginBottom: '1rem' }}>
                    Plak hier data met <strong>EAN</strong> en <strong>inkoopprijs</strong> (één per regel). Scheidingsteken: tab, komma of spatie.
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1rem' }}>
                    Tip: Selecteer in Excel de kolommen EAN en Inkoopprijs, kopieer (Ctrl+C) en plak hier. Excel gebruikt tab als scheidingsteken.
                  </p>
                  <p style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                    Voorbeeld: 8721001690862  12,50 of 8721001690862,12.50
                  </p>
                  <textarea
                    value={inkoopprijsImportData}
                    onChange={(e) => setInkoopprijsImportData(e.target.value)}
                    placeholder="8721001690862  12,50&#10;8721001691791  8.95&#10;8721001691937  15,00"
                    style={{
                      width: '100%',
                      minHeight: '250px',
                      padding: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                  <button className="btn btn-secondary" onClick={() => { setShowInkoopprijsImportModal(false); setInkoopprijsImportData(''); }} disabled={inkoopprijsImportLoading}>
                    Annuleren
                  </button>
                  <button className="btn btn-primary" onClick={handleInkoopprijsPasteImport} disabled={inkoopprijsImportLoading || !inkoopprijsImportData.trim()}>
                    <FiUpload /> {inkoopprijsImportLoading ? 'Importeren...' : 'Importeren'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  Upload een Excel bestand (.xlsx) met minimaal een kolom EAN/barcode en een kolom inkoopprijs.
                </p>
                <p style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1rem' }}>
                  Ondersteunde kolomnamen: EAN, Barcode, GTIN, Artikelnummer, Inkoopprijs, Prijs, Cost. Of: eerste kolom = EAN, tweede numerieke kolom = inkoopprijs.
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleInkoopprijsFileUpload}
                  disabled={inkoopprijsImportLoading}
                  style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }}
                />
                <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                  {inkoopprijsImportLoading ? 'Bestand verwerken...' : 'Selecteer een .xlsx bestand'}
                </p>
              </div>
            )}
          </div>
        </Modal>
      </div>
    );
  };
  
  export default Producten;
